import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Package, ShoppingCart, RefreshCw, Calendar, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ordersAPI } from '../services/api';

const AccountPage = () => {
  const { user } = useAuth();
  const { addToCart, setIsCartOpen } = useCart();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await ordersAPI.getAll();
        setOrders(response.data);
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const handleReorder = (order) => {
    order.items.forEach(item => {
      addToCart({ 
        id: item.productId, 
        name: item.name, 
        price: item.price,
        image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400'
      }, item.quantity);
    });
    setIsCartOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'delivered':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'processing':
        return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'shipped':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default:
        return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  const totalSpent = orders.reduce((sum, o) => sum + o.total, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center">
              <span className="text-black font-bold text-2xl">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{user?.name}</h1>
              <p className="text-zinc-400">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
            <Package className="h-6 w-6 text-amber-500 mb-2" />
            <p className="text-2xl font-bold text-white">{orders.length}</p>
            <p className="text-zinc-400 text-sm">Total Orders</p>
          </div>
          <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
            <ShoppingCart className="h-6 w-6 text-amber-500 mb-2" />
            <p className="text-2xl font-bold text-white">
              £{totalSpent.toFixed(2)}
            </p>
            <p className="text-zinc-400 text-sm">Total Spent</p>
          </div>
          <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800 col-span-2 md:col-span-1">
            <Calendar className="h-6 w-6 text-amber-500 mb-2" />
            <p className="text-2xl font-bold text-white">July 2025</p>
            <p className="text-zinc-400 text-sm">Member Since</p>
          </div>
        </div>

        {/* Orders */}
        <div>
          <h2 className="text-xl font-bold text-white mb-4">Order History</h2>
          
          {orders.length === 0 ? (
            <div className="bg-zinc-900 rounded-xl p-8 border border-zinc-800 text-center">
              <Package className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400">No orders yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => (
                <div
                  key={order.id}
                  className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden"
                >
                  {/* Order header */}
                  <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                    <div>
                      <p className="text-white font-semibold">{order.id}</p>
                      <p className="text-zinc-400 text-sm">{order.date}</p>
                    </div>
                    <Badge className={getStatusColor(order.status)}>
                      {order.status}
                    </Badge>
                  </div>

                  {/* Order items */}
                  <div className="p-4">
                    <div className="space-y-2">
                      {order.items.map((item, index) => (
                        <div
                          key={index}
                          className="flex justify-between text-sm"
                        >
                          <span className="text-zinc-300">
                            {item.name} x{item.quantity}
                          </span>
                          <span className="text-zinc-400">
                            £{(item.price * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-800">
                      <p className="text-white font-semibold">
                        Total: £{order.total.toFixed(2)}
                      </p>
                      <Button
                        size="sm"
                        className="bg-amber-500 hover:bg-amber-600 text-black"
                        onClick={() => handleReorder(order)}
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Reorder
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
